// src/api/sowApi.js
// Enhanced API client for SOW Analysis operations

import api, { apiGet, apiPost, apiPut, apiDelete } from "./client";

// ---------------- RULES ENGINE ----------------
export function fetchRules() {
  return apiGet("/rules/");
}
export function createRule(payload) {
  return apiPost("/rules/", payload);
}
export function updateRule(id, payload) {
  return apiPut(`/rules/${id}`, payload);
}
export function deleteRule(id) {
  return apiDelete(`/rules/${id}`);
}

// ---------------- OPPORTUNITIES ----------------

/**
 * Fetch all opportunities with optional filters
 * @param {Object} params - Filter parameters
 * @param {string} params.query - Search term
 * @param {string} params.contractType - Contract type filter
 * @param {string} params.stage - Stage filter
 * @param {boolean} params.hasSfId - Filter by presence of SF ID
 * @param {number} params.limit - Max results
 * @param {number} params.offset - Pagination offset
 */
export async function fetchOpportunities(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.query) queryParams.append("query", params.query);
  if (params.contractType) queryParams.append("contract_type", params.contractType);
  if (params.stage) queryParams.append("stage", params.stage);
  if (params.hasSfId !== undefined) queryParams.append("has_sf_id", params.hasSfId);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.offset) queryParams.append("offset", params.offset);
  
  const url = `/opportunities${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  return apiGet(url);
}

/**
 * Fetch a single opportunity by ID
 * @param {number} opportunityId
 */
export async function fetchOpportunityById(opportunityId) {
  return apiGet(`/opportunities/${opportunityId}`);
}

/**
 * Get opportunity statistics
 */
export async function fetchOpportunityStats() {
  return apiGet("/opportunities/stats/summary");
}

// ---------------- SOW HISTORY / ANALYSIS ----------------

/**
 * Fetch SOW analysis history with optional filters
 * @param {Object} params - Filter parameters
 * @param {string} params.source - Source filter (SFDC, UI_TEST, HISTORICAL_UPLOAD)
 * @param {number} params.opportunityId - Filter by opportunity ID
 * @param {string} params.sfOpportunityId - Filter by SF Opportunity ID
 * @param {string} params.accountName - Filter by account name
 * @param {string} params.contractType - Filter by contract type
 * @param {number} params.minScore - Minimum overall score
 * @param {number} params.maxScore - Maximum overall score
 * @param {number} params.limit - Max results
 * @param {number} params.offset - Pagination offset
 */
export async function fetchSowHistory(params = {}) {
  const queryParams = new URLSearchParams();
  
  if (params.source) queryParams.append("source", params.source);
  if (params.opportunityId) queryParams.append("opportunity_id", params.opportunityId);
  if (params.sfOpportunityId) queryParams.append("sf_opportunity_id", params.sfOpportunityId);
  if (params.accountName) queryParams.append("account_name", params.accountName);
  if (params.contractType) queryParams.append("contract_type", params.contractType);
  if (params.minScore !== undefined) queryParams.append("min_score", params.minScore);
  if (params.maxScore !== undefined) queryParams.append("max_score", params.maxScore);
  if (params.limit) queryParams.append("limit", params.limit);
  if (params.offset) queryParams.append("offset", params.offset);
  
  const url = `/sow-tests/history${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
  return apiGet(url);
}

/**
 * Fetch historical SOW history (backward compatible)
 * @param {number} limit - Max results (default 50)
 */
export async function fetchHistoricalSowHistory(limit = 50, userEmail = null) {
  const params = new URLSearchParams();
  params.append("limit", limit);
  if (userEmail) params.append("user_email", userEmail);
  return apiGet(`/sow-tests/history?${params.toString()}`);
}

/**
 * Fetch detailed analysis by ID
 * @param {number} historyId
 */
export async function fetchSowHistoryDetail(id, userEmail = null) {
  const params = userEmail ? `?user_email=${encodeURIComponent(userEmail)}` : "";
  return apiGet(`/sow-tests/history/${id}${params}`);
}

/**
 * Analyze a single SOW (test analysis)
 * @param {Object} payload - Analysis payload
 */
export async function analyzeTestSow(payload) {
  return apiPost("/sow-tests/analyze", payload);
}

/**
 * Upload multiple files and analyze (historical lab).
 * - Uses axios upload progress (REAL upload %)
 * - Timeout increased (LLM can take time)
 */
export async function analyzeSowBatchUpload(payload, onProgress) {
  const formData = new FormData();
  formData.append("account_name", payload.account_name);
  if (payload.contract_type) formData.append("contract_type", payload.contract_type);
  if (payload.stage) formData.append("stage", payload.stage);
  if (payload.sf_opportunity_id) formData.append("sf_opportunity_id", payload.sf_opportunity_id);
  if (payload.sow_drafter) formData.append("sow_drafter", payload.sow_drafter);
  if (payload.sow_reviewer) formData.append("sow_reviewer", payload.sow_reviewer);
  if (payload.sl_gd_email) formData.append("sl_gd_email", payload.sl_gd_email);
  if (payload.created_by) formData.append("created_by", payload.created_by);

  (payload.files || []).forEach((f) => formData.append("files", f));

  if (onProgress) onProgress(0, "Preparing upload…");

  const resp = await api.post("/sow-tests/upload-batch", formData, {
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 600000, // 10 min
    onUploadProgress: (evt) => {
      const total = evt.total || 0;
      if (!total) return;
      const pct = Math.round((evt.loaded / total) * 80);
      onProgress && onProgress(pct, "Uploading files…");
    },
  });

  onProgress && onProgress(95, "Analyzing with Rules + LLM + RAG…");
  return resp.data;
}

// ---------------- DOWNLOAD FUNCTIONS ----------------

/**
 * Download Excel report for an analysis
 * @param {number} historyId
 * @returns {Promise<Blob>}
 */
export async function downloadExcelReport(historyId) {
  const resp = await api.get(`/sow-tests/report-excel/${historyId}`, {
    responseType: "blob",
    timeout: 300000,
  });
  return resp.data;
}

/**
 * Download JSON report for an analysis
 * @param {number} historyId
 * @returns {Promise<Blob>}
 */
export async function downloadJsonReport(historyId) {
  const resp = await api.get(`/sow-tests/report-json/${historyId}`, {
    responseType: "blob",
    timeout: 300000,
  });
  return resp.data;
}

/**
 * Download original SOW file
 * @param {number} historyId
 * @returns {Promise<Blob>}
 */
export async function downloadSowFile(historyId) {
  const resp = await api.get(`/sow-tests/files/${historyId}`, {
    responseType: "blob",
    timeout: 300000,
  });
  return resp.data;
}

/**
 * Get analysis statistics
 * @param {string} source - Optional source filter
 */
export async function fetchAnalysisStats(source = null) {
  const url = source 
    ? `/sow-tests/stats/summary?source=${source}` 
    : "/sow-tests/stats/summary";
  return apiGet(url);
}

/**
 * Helper to trigger file download from blob
 * @param {Blob} blob - File blob
 * @param {string} filename - Download filename
 */
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

// ---------------- REPORTS (SOW ANALYSIS REPORTS LIST) ----------------

export function fetchReports(filters = {}) {
  const params = new URLSearchParams(filters);
  const query = params.toString();
  return apiGet(`/reports/${query ? `?${query}` : ""}`);
}

export function fetchReportDetail(id) {
  return apiGet(`/reports/${id}`);
}